import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CalendarClock, CheckCircle2, CheckCheck, Circle, PlayCircle, Send, Clock, RotateCcw,
  ExternalLink, AlertTriangle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import {
  getPlan, getSessionPlan,
  type LearnerPlanDto, type AssessmentItemDto, type SessionPlanDto, type M36Support, type StatusValue,
} from './learnerPlanApi';

// BP 25 learner plan feature — public surface. DC Connect (D2L) is the system
// of record for every one of these instruments; everything below is either a
// reference record, a class-time anchor, or a labelled mirror of D2L status.
// Nothing here submits, marks, or grades anything.

const M36_LOCAL_KEY = 'acechem-m36-self-check';

/** Human labels for every AssessmentStatus value — the single source both
 * the student-facing badge below and the instructor override picker
 * (features/learnerPlan/components/StatusOverrideDialog) draw from, so the
 * two surfaces can never drift apart on what a status is called. */
export const STATUS_VALUE_LABELS: Record<string, string> = {
  not_started: 'Not started',
  in_progress: 'In progress',
  submitted: 'Submitted',
  awaiting_results: 'Awaiting results',
  passed: 'Passed',
  needs_retry: 'Needs retry',
  graded: 'Graded',
};

function statusLabel(status: AssessmentItemDto['my_status']): string {
  if (!status) return 'Not started';
  return STATUS_VALUE_LABELS[status.status] || status.status;
}

/** Per-status icon + colour. The icon carries the state as FORM, not just
 * colour — a controls audit flagged the old oval badge as colour-only. Tones
 * are grouped by meaning (not-yet-done / in motion / waiting / done / retry),
 * each drawn from --ws-* status tokens so the pairing survives every theme. */
const STATUS_META: Record<StatusValue, { icon: LucideIcon; tone: string }> = {
  not_started: { icon: Circle, tone: 'border-ws-border bg-ws-surface-2 text-ws-ink-3' },
  in_progress: { icon: PlayCircle, tone: 'border-ws-accent/30 bg-ws-accent-quiet text-ws-accent-ink' },
  submitted: { icon: Send, tone: 'border-ws-accent/30 bg-ws-accent-quiet text-ws-accent-ink' },
  awaiting_results: { icon: Clock, tone: 'border-ws-gold/40 bg-ws-gold-quiet text-ws-gold-ink' },
  passed: { icon: CheckCircle2, tone: 'border-ws-success/30 bg-ws-success-quiet text-ws-success' },
  graded: { icon: CheckCheck, tone: 'border-ws-success/30 bg-ws-success-quiet text-ws-success' },
  needs_retry: { icon: RotateCcw, tone: 'border-ws-danger/30 bg-ws-danger/10 text-ws-danger' },
};

/** A small badge that always tells the truth about WHERE a status came from
 * and WHEN it was observed — an imported row is never allowed to read as
 * first-hand ACE evidence (BP 25/27). Modern rounded-rectangle control (not
 * an oval pill), and every state carries its own icon so it never relies on
 * colour alone. */
export function StatusBadge({ status }: { status: AssessmentItemDto['my_status'] }) {
  const meta = STATUS_META[status?.status ?? 'not_started'] ?? STATUS_META.not_started;
  const Icon = meta.icon;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11.5px] font-semibold ${meta.tone}`}>
        <Icon size={13} strokeWidth={2.5} aria-hidden="true" />
        {statusLabel(status)}
      </span>
      {status && (
        <span className="text-[10px] text-ws-ink-3">
          {status.source === 'd2l_import' ? 'D2L · imported ' : 'Marked '}
          {new Date(status.observed_at).toLocaleDateString()}
        </span>
      )}
    </span>
  );
}

/** The compact class-time summary of the plan (what's open/due around today).
 * Reads its own state — takes no props. */
export function LearnerPlanCard() {
  const [plan, setPlan] = useState<LearnerPlanDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getPlan()
      .then((data) => { if (!cancelled) setPlan(data); })
      .catch(() => { if (!cancelled) setPlan(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <Card className="p-4">
        <Spinner label="Loading learner plan…" />
      </Card>
    );
  }
  if (!plan || plan.items.length === 0) return null;

  const outstanding = plan.items
    .filter((it) => it.due_at && (!it.my_status || !['passed', 'graded'].includes(it.my_status.status)))
    .slice(0, 4);

  return (
    <Card className="p-4">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-sm font-semibold text-ws-ink">
          <CalendarClock size={15} className="text-ws-accent" />
          Learner plan
        </h2>
        <Link to="/learner-plan" className="text-xs font-medium text-ws-accent hover:underline">
          View all
        </Link>
      </div>

      {outstanding.length === 0 ? (
        <p className="text-[13px] text-ws-ink-3">Nothing outstanding right now.</p>
      ) : (
        <ul className="space-y-1.5">
          {outstanding.map((it) => (
            <li key={it.key} className="flex items-center justify-between gap-2 text-[13px]">
              <span className="min-w-0 truncate text-ws-ink-2">{it.title}</span>
              <StatusBadge status={it.my_status} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function ItemChip({ item }: { item: Pick<AssessmentItemDto, 'key' | 'title' | 'points' | 'd2l_url'> }) {
  return (
    <li className="flex items-center justify-between gap-2 rounded-md border border-ws-border bg-ws-surface px-2.5 py-1.5 text-[13px]">
      <span className="min-w-0 truncate text-ws-ink-2">
        {item.title}
        {item.points != null && <span className="ml-1 text-ws-ink-3">· {item.points} pts</span>}
      </span>
      {item.d2l_url && (
        <a href={item.d2l_url} target="_blank" rel="noreferrer" className="shrink-0 text-ws-accent hover:underline">
          <ExternalLink size={13} />
        </a>
      )}
    </li>
  );
}

function PlanBucket({ label, items }: { label: string; items: SessionPlanDto[keyof Omit<SessionPlanDto, 'blockers'>] }) {
  if (!items.length) return null;
  return (
    <div>
      <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ws-ink-3">{label}</h3>
      <ul className="space-y-1">
        {items.map((it) => <ItemChip key={it.key} item={it} />)}
      </ul>
    </div>
  );
}

/** A meeting-scoped strip resolving introduce/worktime/remind/due items, for
 * the BP 22 run-sheet. Instructor-only endpoint underneath. */
export function PlanStrip({ classSessionId }: { classSessionId: string }) {
  const [plan, setPlan] = useState<SessionPlanDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getSessionPlan(classSessionId)
      .then((data) => { if (!cancelled) setPlan(data); })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [classSessionId]);

  if (loading) return <Spinner label="Loading plan…" />;
  if (error || !plan) return null;

  const empty = !plan.introduce.length && !plan.worktime.length && !plan.remind.length && !plan.due.length;

  return (
    <Card className="p-4" aria-label="Learner plan for this meeting">
      {empty && plan.blockers.length === 0 ? (
        <p className="text-[13px] text-ws-ink-3">Nothing from the learner plan is anchored to this meeting.</p>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <PlanBucket label="Introduce" items={plan.introduce} />
          <PlanBucket label="Work time" items={plan.worktime} />
          <PlanBucket label="Remind" items={plan.remind} />
          <PlanBucket label="Due" items={plan.due} />
        </div>
      )}

      {plan.blockers.map((blocker) => (
        blocker.students.length > 0 && (
          <div key={blocker.item_key} className="mt-3 rounded-lg border border-ws-danger/30 bg-ws-danger/10 p-3">
            <h3 className="flex items-center gap-1.5 text-[12px] font-semibold text-ws-danger">
              <AlertTriangle size={13} />
              Cannot write {blocker.gates_item_key} yet
            </h3>
            <ul className="mt-1.5 space-y-1">
              {blocker.students.map((s) => (
                <li key={`${blocker.item_key}-${s.student_id ?? s.name}`} className="flex items-center justify-between text-[12.5px] text-ws-ink-2">
                  <span>{s.name || s.student_id || 'Unknown student'}</span>
                  <span className="inline-flex items-center gap-1 text-[11px] text-ws-ink-3">
                    {s.status.replace('_', ' ')}
                    <span className="rounded-md bg-ws-surface-2 px-1.5 py-0.5">
                      observed {new Date(s.observed_at).toLocaleDateString()}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )
      ))}
    </Card>
  );
}

function readLocalChecks(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(M36_LOCAL_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}
function writeLocalChecks(v: Record<string, boolean>) {
  try { window.localStorage.setItem(M36_LOCAL_KEY, JSON.stringify(v)); } catch { /* ignore */ }
}

/** The Milestone 36 support checklist + reflection questions — a personal
 * self-check (stored per-viewer only; students still submit in D2L). */
export function M36Checklist() {
  const [plan, setPlan] = useState<LearnerPlanDto | null>(null);
  const [checks, setChecks] = useState<Record<string, boolean>>(() => readLocalChecks());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getPlan()
      .then((data) => { if (!cancelled) setPlan(data); })
      .catch(() => { if (!cancelled) setPlan(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <Spinner label="Loading checklist…" />;

  const m36 = plan?.items.find((it) => it.key === 'm36');
  const support = (m36?.support || {}) as M36Support;
  if (!support.sections || support.sections.length === 0) return null;

  const toggle = (id: string) => {
    const next = { ...checks, [id]: !checks[id] };
    setChecks(next);
    writeLocalChecks(next);
  };

  return (
    <Card className="p-4">
      <h2 className="text-sm font-semibold text-ws-ink">Milestone 36 — Elemental Showcase checklist</h2>
      {support.overview && <p className="mt-1 text-[13px] text-ws-ink-3">{support.overview}</p>}

      <div className="mt-3 space-y-3">
        {support.sections.map((section, si) => (
          <div key={section.title}>
            <h3 className="text-[13px] font-semibold text-ws-ink-2">{section.title}</h3>
            <ul className="mt-1 space-y-1">
              {section.requirements.map((req, ri) => {
                const id = `${si}-${ri}`;
                const done = !!checks[id];
                return (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => toggle(id)}
                      aria-pressed={done}
                      className="flex w-full items-start gap-2 rounded-md p-1 text-left text-[12.5px] text-ws-ink-2 hover:bg-ws-surface-2"
                    >
                      {done ? <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-ws-success" /> : <Circle size={15} className="mt-0.5 shrink-0 text-ws-ink-3" />}
                      <span className={done ? 'line-through text-ws-ink-3' : ''}>{req}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>

      {support.reflection_questions && support.reflection_questions.length > 0 && (
        <div className="mt-3">
          <h3 className="text-[13px] font-semibold text-ws-ink-2">Reflection (submitted with your upload in D2L)</h3>
          <ol className="mt-1 list-decimal space-y-1 pl-4 text-[12.5px] text-ws-ink-2">
            {support.reflection_questions.map((q) => <li key={q}>{q}</li>)}
          </ol>
        </div>
      )}
    </Card>
  );
}
